import { Currency } from "@redotech/money/currencies";
import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import {
  LoyaltyEarningRuleType,
  LoyaltyPointAdjustmentReason,
  LoyaltyPointAdjustmentType,
} from "@redotech/redo-loyalty-common/loyalty-definition";
import { CompleteOrderFulfillmentStatus } from "@redotech/redo-shopify-order-common/shopify-order";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { MarketingEntryPoint } from "../advanced-flow/schemas/marketing/marketing";
import { dateOrStringAsDate, yyyyMmDdSchema } from "../common/zod-util";
import { ConversationStatus } from "../conversation";
import { delayOptionSchema } from "../email-builder/subscription-utils";
import { Provider } from "../order";
import { returnStatusJsonFormat } from "../return-new";
import { SubscriptionIntegrationType } from "../subscription-integrations/subscription-integration";
import type { CustomerEvent } from "./customer-event-definition";
import {
  attributableDiscountCodesSchema,
  baseCustomerEventSchema,
  CustomerEventType,
} from "./events/base";
import { rawCheckoutRatesShownEventSchemaShape } from "./events/checkout-rates-shown";
import {
  InboundReceivedEventSchema_,
  OutreachBounceEventSchema_,
  OutreachClickEventSchema_,
  OutreachComplaintEventSchema_,
  OutreachDeliveryEventSchema_,
  OutreachFailedEventSchema_,
  OutreachOpenEventSchema_,
  OutreachSendEventSchema_,
} from "./events/outreach";
import {
  CheckoutCompletedShoppingEventSchema_,
  CheckoutContactInfoSubmittedShoppingEventSchema_,
  CheckoutInfoChangedShoppingEventSchema_,
  CheckoutProbabilityCalculatedShoppingEventSchema_,
  CheckoutStartedShoppingEventSchema_,
  CollectionViewedShoppingEventSchema_,
  PackageProtectionPriceExposedShoppingEventSchema_,
  PaymentFailedShoppingEventSchema_,
  ProductAddedToCartShoppingEventSchema_,
  ProductRemovedFromCartShoppingEventSchema_,
  ProductViewedShoppingEventSchema_,
  StorePageViewedShoppingEventSchema_,
} from "./events/pixel";
import { redoStorefrontEventMoneySchema } from "./events/storefront-event-primitives";
import {
  customEventPayloadSchema,
  trackingPageClickedPayloadSchema,
  trackingPageViewedPayloadSchema,
} from "./widget-event-payloads";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  CustomerEventSource,
  CustomerEventType,
  ImportSource,
  PaymentFailureStage,
} from "./events/base";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type { BaseCustomerEvent } from "./events/base";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type * from "./events/outreach";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { BounceType, ReputationImpact } from "./events/outreach";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export * from "./events/ses-events";
// no importing from ./definition.ts in this file - otherwise we get circular dependencies

export const CUSTOMER_EVENT_DISCRIMINATOR_KEY = "eventType" as const;

// TODO: break this file up into smaller files, with anything specific to a specific event type being moved to that event type's file

// ###
// ProductSubscriptionActionEvent
// ###
export enum ProductSubscriptionActionType {
  DELAY = "delay",
  SKIP = "skip",
}

export enum ExternalCustomerPlatform {
  SHOPIFY = "shopify",
}

export enum UnsubscribeSource {
  SHOPIFY = "shopify",
  COMMENTSOLD = "commentsold",
  EMAIL = "email",
  ONE_CLICK_EMAIL = "one-click-email",
  SMS_STOP_MESSAGE = "sms-stop-message",
  MERCHANT = "merchant",
  SMS_LINK = "sms-link",
  PUBLIC_API = "public-api",
  IMPORT = "import",
}

export enum MarketingConfirmSource {
  AUTOCONFIRM = "autoconfirm",
  BACKFILL = "backfill",
  CSV_IMPORT = "csv-import",
  MERCHANT = "merchant",
  SMS_MESSAGE = "sms-message",
  TEXT_TO_SUBSCRIBE = "text-to-subscribe",
  PUBLIC_API = "public-api",
}

export enum SubscriptionUpdateOutcome {
  UPDATED = "updated",
  ADDED = "added",
  NO_OP = "no-op",
}

const OrderLineItemSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
});

const shopifyAddressSchema = z.object({
  name: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  province: z.string().nullish(),
  province_code: z.string().nullish(),
  country: z.string().nullish(),
  country_code: z.string().nullish(),
  zip: z.string().nullish(),
  phone: z.string().nullish(),
  company: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
});

const OrderCreatedCustomerEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.ORDER_CREATED),
  provider: createEnum(Provider, { enumName: "Provider" }).nullish(),
  orderId: zExt.objectId(),
  shopifyId: z.string(),
  itemCount: z.number(),
  totalPrice: z.string(),
  orderName: z.string(),
  sourceName: z.string().optional(),
  currency: createEnum(Currency, { enumName: "Currency" }).optional(),
  lineItems: z.array(OrderLineItemSchema).optional(),
  billingAddress: shopifyAddressSchema.nullish(),
  usedRedoCoverage: z.boolean().optional(),
  discountCodes: attributableDiscountCodesSchema,
  // ---
  // A/B test attribution fields (non-Shop-Plus granular analytics).
  // All nullish / backward-compatible. Populated by the order webhook
  // at ORDER_CREATED enqueue time (see parseOrderCreatedEventFromOrder
  // in redo/orders/db/util/src/order-events.ts).
  // ---
  /** Shipping address on the order. */
  shippingAddress: shopifyAddressSchema.nullish(),
  /**
   * Hash of (teamId + normalized shipping address). Same value computed
   * server-side from CHECKOUT_RATES_SHOWN visits so the warehouse can
   * join visits to completions without a checkout token.
   */
  addressFingerprint: z.string().nullish(),
  /**
   * Treatment IDs the shopper converted on — parsed from the Redo-formatted
   * shipping service code of the line item they selected.
   */
  assignedTreatmentIds: z.array(z.string()).nullish(),
  /**
   * Total shipping revenue from the purchased shipping line(s), in shop
   * currency. Stored as a raw scalar; any per-treatment splitting is the
   * warehouse / dbt layer's responsibility — customer events store
   * unopinionated raw data.
   */
  shippingRevenue: z.number().nullish(),
});
export const OrderCreatedCustomerEventSchema: z.ZodType<OrderCreatedCustomerEvent> =
  OrderCreatedCustomerEventSchema_;
export interface OrderCreatedCustomerEvent extends z.infer<
  typeof OrderCreatedCustomerEventSchema_
> {}

const CheckoutRatesShownCustomerEventSchema_ = baseCustomerEventSchema
  .extend({ eventType: z.literal(CustomerEventType.CHECKOUT_RATES_SHOWN) })
  .extend(rawCheckoutRatesShownEventSchemaShape.shape);
export const CheckoutRatesShownCustomerEventSchema: z.ZodType<CheckoutRatesShownCustomerEvent> =
  CheckoutRatesShownCustomerEventSchema_;
export interface CheckoutRatesShownCustomerEvent extends z.infer<
  typeof CheckoutRatesShownCustomerEventSchema_
> {}

const OrderUpdatedCustomerEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.ORDER_UPDATED),
  orderId: zExt.objectId(),
  shopifyId: z.string(),
});
export const OrderUpdatedCustomerEventSchema: z.ZodType<OrderUpdatedCustomerEvent> =
  OrderUpdatedCustomerEventSchema_;
export interface OrderUpdatedCustomerEvent extends z.infer<
  typeof OrderUpdatedCustomerEventSchema_
> {}

const OrderStatusUpdatedCustomerEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.ORDER_STATUS_UPDATED),
  orderId: zExt.objectId(),
  shopifyId: z.string(),
  newStatus: CompleteOrderFulfillmentStatus.zodParser,
});
export const OrderStatusUpdatedCustomerEventSchema: z.ZodType<OrderStatusUpdatedCustomerEvent> =
  OrderStatusUpdatedCustomerEventSchema_;
export interface OrderStatusUpdatedCustomerEvent extends z.infer<
  typeof OrderStatusUpdatedCustomerEventSchema_
> {}

const ReturnCreatedCustomerEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.RETURN_CREATED),
  returnId: zExt.objectId(),
});

const ReturnStatusUpdatedCustomerEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.RETURN_STATUS_UPDATED),
  returnId: zExt.objectId(),
  newStatus: z
    .string()
    .nullable()
    .transform((str) => {
      return returnStatusJsonFormat.read(str);
    }),
});

const ReturnCommentCreatedCustomerEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.RETURN_COMMENT_CREATED),
  returnId: zExt.objectId(),
  messageId: zExt.objectId(),
});

const ReturnProcessedCustomerEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.RETURN_PROCESSED),
  returnId: zExt.objectId(),
});

const ClaimCreatedCustomerEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.CLAIM_CREATED),
  claimId: zExt.objectId(),
});

const ClaimStatusUpdatedCustomerEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.CLAIM_STATUS_UPDATED),
  claimId: zExt.objectId(),
  newStatus: z
    .string()
    .nullable()
    .transform((str) => {
      return returnStatusJsonFormat.read(str);
    }),
});

const ClaimCommentCreatedCustomerEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.CLAIM_COMMENT_CREATED),
  claimId: zExt.objectId(),
  messageId: zExt.objectId(),
});

const ConversationCreatedCustomerEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.CONVERSATION_CREATED),
  conversationId: zExt.objectId(),
});

const ConversationStatusUpdatedCustomerEventSchema =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.CONVERSATION_STATUS_UPDATED),
    conversationId: zExt.objectId(),
    newStatus: createEnum(ConversationStatus, {
      enumName: "ConversationStatus",
    }),
  });

const ConversationMessageCreatedCustomerEventSchema =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.CONVERSATION_MESSAGE_CREATED),
    conversationId: zExt.objectId(),
    messageId: zExt.objectId(),
  });

const ExternalPlatformCustomerCreatedCustomerEventSchema =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.EXTERNAL_PLATFORM_CUSTOMER_CREATED),
    externalPlatformCustomerId: z.string(),
    externalPlatform: createEnum(ExternalCustomerPlatform, {
      enumName: "ExternalCustomerPlatform",
    }),
  });

const StorefrontLoginCustomerEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.STOREFRONT_LOGIN),
  multipassLogin: z.boolean(),
});

const MarketingSubscriptionBaseEventSchema = baseCustomerEventSchema.extend({
  updateOutcome: createEnum(SubscriptionUpdateOutcome, {
    enumName: "SubscriptionUpdateOutcome",
  }).nullish(),
  channel: createEnum(MarketingChannel, {
    enumName: "MarketingChannel",
  }).nullish(),
  channelId: z.string().nullish(),
  skipAutomations: z.boolean().optional(),
});

// Loose schema — accepts string or object. Full signup form validation
// lives in @redotech/redo-marketing-common/signup-form/signup-form-schema.
// Fix when this lives in redo/marketing
const formSnapshotSchema = z
  .string()
  .or(z.record(z.string(), z.unknown()))
  .optional()
  .catch(() => undefined);

export type FormSnapshot = z.infer<typeof formSnapshotSchema>;

const MarketingSubscribeEventSchema =
  MarketingSubscriptionBaseEventSchema.extend({
    eventType: z.literal(CustomerEventType.MARKETING_SUBSCRIBED),
    email: z.string().optional(),
    phoneNumber: z.string().optional(),
    entryPoint: createEnum(MarketingEntryPoint, {
      enumName: "MarketingEntryPoint",
    }),
    formId: z
      .string()
      .or(zExt.objectId())
      .transform((id) => id?.toString())
      .optional(),
    subscribeKeywordId: z.string().optional(),
    formSnapshot: formSnapshotSchema,
    ipAddress: z.string().optional(),
    // TODO: migrate to ipAddress field name to match other events (pixel events, outreach events, etc.)
    customerIpAddress: z.string().optional(),
    discountCode: z.string().optional(),
    discountCodes: attributableDiscountCodesSchema,
    treatmentId: zExt.objectId().optional(),
    language: z.string().optional(),
    country: z.string().optional(),
  });
export type MarketingSubscribeEvent = z.infer<
  typeof MarketingSubscribeEventSchema
>;

const MarketingUnsubscribeEventSchema =
  MarketingSubscriptionBaseEventSchema.extend({
    eventType: z.literal(CustomerEventType.MARKETING_UNSUBSCRIBED),
    subscriptionType: createEnum(MarketingChannel, {
      enumName: "MarketingChannel",
    }),
    source: createEnum(UnsubscribeSource, {
      enumName: "UnsubscribeSource",
    }).optional(),
    campaignId: z.string().optional(),
    automationId: z.string().optional(),
    automationStepId: z.string().optional(),
    templateId: z.string().optional(),
  });
export type MarketingUnsubscribeEvent = z.infer<
  typeof MarketingUnsubscribeEventSchema
>;

const MarketingConfirmEventSchema = MarketingSubscriptionBaseEventSchema.extend(
  {
    eventType: z.literal(CustomerEventType.MARKETING_CONFIRMED),
    entryPoint: createEnum(MarketingEntryPoint, {
      enumName: "MarketingEntryPoint",
    }).nullish(),
    subscriptionType: createEnum(MarketingChannel, {
      enumName: "MarketingChannel",
    }),
    source: createEnum(MarketingConfirmSource, {
      enumName: "MarketingConfirmSource",
    }).nullish(),
    subscribeKeywordId: z.string().optional(),
  },
);
export type MarketingConfirmEvent = z.infer<typeof MarketingConfirmEventSchema>;

const MarketingEventSchema = z.discriminatedUnion("eventType", [
  MarketingSubscribeEventSchema,
  MarketingUnsubscribeEventSchema,
  MarketingConfirmEventSchema,
]);
export type MarketingEvent = z.infer<typeof MarketingEventSchema>;

const _CustomerGroupMembershipBaseSchema = baseCustomerEventSchema.extend({
  customerGroupId: zExt.objectId(),
});

const CustomerGroupEnterEventSchema = _CustomerGroupMembershipBaseSchema.extend(
  { eventType: z.literal(CustomerEventType.CUSTOMER_GROUP_ENTER) },
);

const CustomerGroupExitEventSchema = _CustomerGroupMembershipBaseSchema.extend({
  eventType: z.literal(CustomerEventType.CUSTOMER_GROUP_EXIT),
});

const CustomerInformationUpdatedEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.CUSTOMER_INFORMATION_UPDATE),
  newFirstName: z.string().optional(),
  newLastName: z.string().optional(),
  newPhoneNumber: z.string().optional(),
});

const OmsOrderReadyForPickupEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.OMS_ORDER_READY_FOR_PICKUP),
  fulfillmentGroupId: z.string(),
  fulfillmentOrderId: z.string().nullish(),
});

const AddressVerificationFailedEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.ADDRESS_VERIFICATION_FAILED),
  fulfillmentGroupId: z.string(),
  originalAddress: shopifyAddressSchema,
  errorMessages: z.array(z.string()),
  email: z.string(),
  merchantName: z.string(),
});

const _LoyaltyPointsEventSchema = baseCustomerEventSchema.extend({
  // Make customer required on these types of events.
  customer: zExt.objectId().transform((id) => id.toString()),
  finalPointAmount: z.number().int(),
});

const LoyaltyPointsRedeemEventSchema = _LoyaltyPointsEventSchema.extend({
  eventType: z.literal(CustomerEventType.LOYALTY_REDEEM),
  orderId: zExt.objectId().transform((id) => id.toString()),
  finalPointAmount: z.number().int().negative(),
  discountCodes: attributableDiscountCodesSchema,
});

export const LoyaltyPointsReturnRefundEventSchema =
  _LoyaltyPointsEventSchema.extend({
    eventType: z.literal(CustomerEventType.LOYALTY_RETURN_REFUND),
    finalPointAmount: z.number().int().positive(),
    returnId: zExt.objectId().transform((id) => id.toString()),
  });

const LoyaltyPointsReturnClawbackEventSchema = _LoyaltyPointsEventSchema.extend(
  {
    eventType: z.literal(CustomerEventType.LOYALTY_RETURN_CLAWBACK),
    finalPointAmount: z.number().int().negative(),
    returnId: zExt.objectId().transform((id) => id.toString()),
  },
);

const LoyaltyPointsCancelPurchaseRefundEventSchema =
  _LoyaltyPointsEventSchema.extend({
    eventType: z.literal(CustomerEventType.LOYALTY_CANCEL_PURCHASE_REFUND),
    finalPointAmount: z.number().int().positive(),
    orderId: zExt.objectId().transform((id) => id.toString()),
  });

const LoyaltyPointsCancelPurchaseClawbackEventSchema =
  _LoyaltyPointsEventSchema.extend({
    eventType: z.literal(CustomerEventType.LOYALTY_CANCEL_PURCHASE_CLAWBACK),
    finalPointAmount: z.number().int().negative(),
    orderId: zExt.objectId().transform((id) => id.toString()),
  });

const LoyaltyPointsManualAdjustmentEventSchema =
  _LoyaltyPointsEventSchema.extend({
    eventType: z.literal(CustomerEventType.LOYALTY_MANUAL_ADJUSTMENT),
    reason: z.string(),
  });

const LoyaltyPointsExpireEventSchema = _LoyaltyPointsEventSchema.extend({
  eventType: z.literal(CustomerEventType.LOYALTY_EXPIRE),
  finalPointAmount: z.number().int().negative(),
});

const _LoyaltyPointsBaseEarnEventSchema = _LoyaltyPointsEventSchema.extend({
  earningRuleType: createEnum(LoyaltyEarningRuleType, {
    enumName: "LoyaltyEarningRuleType",
  }),
  basePointAmount: z.number().int().positive(),
  pointAdjustments: z.array(
    z.object({
      adjustmentReason: createEnum(LoyaltyPointAdjustmentReason, {
        enumName: "LoyaltyPointAdjustmentReason",
      }),
      adjustmentType: createEnum(LoyaltyPointAdjustmentType, {
        enumName: "LoyaltyPointAdjustmentType",
      }),
      adjustmentAmount: z.number().positive(),
    }),
  ),
  finalPointAmount: z.number().int().positive(),
  expirationDate: dateOrStringAsDate,
  appliedPromotionsIds: z
    .array(zExt.objectId())
    .transform((ids) => ids.map((id) => id.toString())),
});

const LoyaltyPointsEarnEventSchema = _LoyaltyPointsBaseEarnEventSchema.extend({
  eventType: z.literal(CustomerEventType.LOYALTY_EARN),
});

const LoyaltyPointsEarnReviewEventSchema =
  _LoyaltyPointsBaseEarnEventSchema.extend({
    eventType: z.literal(CustomerEventType.LOYALTY_EARN_REVIEW),
    earningRuleType: z.literal(LoyaltyEarningRuleType.REVIEW),
    reviewId: zExt.objectId().transform((id) => id.toString()),
  });

const LoyaltyPointsEarnPurchaseEventSchema =
  _LoyaltyPointsBaseEarnEventSchema.extend({
    eventType: z.literal(CustomerEventType.LOYALTY_EARN_PURCHASE),
    earningRuleType: z.literal(LoyaltyEarningRuleType.PURCHASE),
    orderId: zExt.objectId().transform((id) => id.toString()),
    shopifyOrderLineItemId: z.string(),
  });

const baseSubscriptionActionEventSchema = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.PRODUCT_SUBSCRIPTION_ACTION),
  subscriptionIds: z.array(z.string()),
  emailId: z.string(),
  nextChargeDate: z.object({
    original: yyyyMmDdSchema,
    new: yyyyMmDdSchema.nullish(),
  }),
  chargeId: z.string(),
});

const ProductSubscriptionDelayedEventSchema =
  baseSubscriptionActionEventSchema.extend({
    actionType: z.literal(ProductSubscriptionActionType.DELAY),
    delayOption: delayOptionSchema,
  });

const ProductSubscriptionSkippedEventSchema =
  baseSubscriptionActionEventSchema.extend({
    actionType: z.literal(ProductSubscriptionActionType.SKIP),
  });

export const ProductSubscriptionActionEventSchema = z.discriminatedUnion(
  "actionType",
  [
    ProductSubscriptionDelayedEventSchema,
    ProductSubscriptionSkippedEventSchema,
  ],
);

// ###
// BackInStockCustomerEventSchemas
// ###
const SubscribedToBackInStockEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.SUBSCRIBED_TO_BACK_IN_STOCK),
  productId: z.string(),
  variantId: z.string(),
  customerProductNotificationId: zExt
    .objectId()
    .transform((id) => id.toString())
    .nullish(),
});
export const SubscribedToBackInStockEventSchema: z.ZodType<SubscribedToBackInStockEvent> =
  SubscribedToBackInStockEventSchema_;
export type SubscribedToBackInStockEvent = z.infer<
  typeof SubscribedToBackInStockEventSchema_
>;

const TriggeredBackInStockNotificationEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(
      CustomerEventType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION,
    ),
    productId: z.string(),
    variantId: z.string(),
    productHandle: z.string().nullish(),
    productTitle: z.string().nullish(),
    oldInventoryCount: z.number().nullish(),
    newInventoryCount: z.number().nullish(),
  });
export const TriggeredBackInStockNotificationEventSchema: z.ZodType<TriggeredBackInStockNotificationEvent> =
  TriggeredBackInStockNotificationEventSchema_;
export interface TriggeredBackInStockNotificationEvent extends z.infer<
  typeof TriggeredBackInStockNotificationEventSchema_
> {}

const PersonaAssignmentEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.PERSONA_ASSIGNMENT),
  personaId: z.string(),
  personaName: z.string(),
  confidence: z.number().min(1).max(100),
  reasoning: z.string(),
});
export const PersonaAssignmentEventSchema: z.ZodType<PersonaAssignmentEvent> =
  PersonaAssignmentEventSchema_;
export interface PersonaAssignmentEvent extends z.infer<
  typeof PersonaAssignmentEventSchema_
> {}

const SignupFormViewedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.SIGNUP_FORM_VIEWED),
  formId: zExt.objectId(),
  treatmentId: zExt.objectId().optional(),
});
export const SignupFormViewedEventSchema: z.ZodType<SignupFormViewedEvent> =
  SignupFormViewedEventSchema_;
export interface SignupFormViewedEvent extends z.infer<
  typeof SignupFormViewedEventSchema_
> {}

const SignupFormSubmittedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.SIGNUP_FORM_SUBMITTED),
  formId: zExt.objectId(),
  treatmentId: zExt.objectId().optional(),
});
export const SignupFormSubmittedEventSchema: z.ZodType<SignupFormSubmittedEvent> =
  SignupFormSubmittedEventSchema_;
export interface SignupFormSubmittedEvent extends z.infer<
  typeof SignupFormSubmittedEventSchema_
> {}

const RescuePanelViewedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.RESCUE_PANEL_VIEWED),
  sessionId: z.string(),
  itemQuantity: z.number().int().nonnegative(),
});
export const RescuePanelViewedEventSchema: z.ZodType<RescuePanelViewedEvent> =
  RescuePanelViewedEventSchema_;
export interface RescuePanelViewedEvent extends z.infer<
  typeof RescuePanelViewedEventSchema_
> {}

const RescuePanelCtaClickedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.RESCUE_PANEL_CTA_CLICKED),
  sessionId: z.string(),
  itemQuantity: z.number().int().nonnegative(),
});
export const RescuePanelCtaClickedEventSchema: z.ZodType<RescuePanelCtaClickedEvent> =
  RescuePanelCtaClickedEventSchema_;
export interface RescuePanelCtaClickedEvent extends z.infer<
  typeof RescuePanelCtaClickedEventSchema_
> {}

// ###
// FailedDiscountDetectedEvent
// ###
// Emitted when a shopper's discount code is rejected at checkout and the
// failed-discount-rescue widget is triggered (detection == widget shown). The
// session/anonymous/shopify identifiers are inherited from the base schema and
// are only present when the Web Pixel had resolved them by attempt time —
// failed-code attempts are often the buyer's first interaction, before any
// identity is known. `widgetId` is absent on detection (the pixel route is
// team-scoped, not checkout-widget-scoped); the join key downstream is
// `checkoutToken`.
const FailedDiscountDetectedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.FAILED_DISCOUNT_DETECTED),
  sessionId: z.string().optional(),
  checkoutToken: z.string(),
  widgetId: z.string().optional(),
  /** Buyer's typed code, or null when the platform didn't surface it. */
  attemptedCode: z.string().nullable(),
});
export const FailedDiscountDetectedEventSchema: z.ZodType<FailedDiscountDetectedEvent> =
  FailedDiscountDetectedEventSchema_;
export interface FailedDiscountDetectedEvent extends z.infer<
  typeof FailedDiscountDetectedEventSchema_
> {}

// ###
// FailedDiscountRescueClaimedEvent
// ###
// Emitted when the shopper clicks "Claim" and the replacement discount code is
// successfully resolved. Identifiers are recovered from the detection record
// cached server-side by `checkoutToken` (the claim RPC itself carries no
// customer identifiers), so they are present whenever detection had them.
const FailedDiscountRescueClaimedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.FAILED_DISCOUNT_RESCUE_CLAIMED),
  sessionId: z.string().optional(),
  checkoutToken: z.string(),
  widgetId: z.string(),
  discountId: z.string(),
  /** The resolved replacement code handed to the shopper. */
  discountCode: z.string(),
});
export const FailedDiscountRescueClaimedEventSchema: z.ZodType<FailedDiscountRescueClaimedEvent> =
  FailedDiscountRescueClaimedEventSchema_;
export interface FailedDiscountRescueClaimedEvent extends z.infer<
  typeof FailedDiscountRescueClaimedEventSchema_
> {}

const ProductVisualizationGeneratedEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.PRODUCT_VISUALIZATION_GENERATED),
    conversationId: zExt.objectId().transform((id) => id.toString()),
    productId: z.string(),
    variantId: z.string().optional(),
    visualizationUrl: z.string(),
  });
export const ProductVisualizationGeneratedEventSchema: z.ZodType<ProductVisualizationGeneratedEvent> =
  ProductVisualizationGeneratedEventSchema_;
export interface ProductVisualizationGeneratedEvent extends z.infer<
  typeof ProductVisualizationGeneratedEventSchema_
> {}

const MeaningfulRecoverConversationEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.MEANINGFUL_RECOVER_CONVERSATION),
    marketingConversationId: zExt.objectId().transform((id) => id.toString()),
  });
export const MeaningfulRecoverConversationEventSchema: z.ZodType<MeaningfulRecoverConversationEvent> =
  MeaningfulRecoverConversationEventSchema_;
export interface MeaningfulRecoverConversationEvent extends z.infer<
  typeof MeaningfulRecoverConversationEventSchema_
> {}

/**
 * Shared fields on shopper agent attribution events; channelThreadId ties the
 * event to the channel thread where the action happened.
 */
const shopperAgentAttributionFields = {
  channel: createEnum(MarketingChannel, { enumName: "MarketingChannel" }),
  channelThreadId: z.string(),
};

const ShopperAgentReturnCreatedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.SHOPPER_AGENT_RETURN_CREATED),
  ...shopperAgentAttributionFields,
  returnId: z.string(),
  orderId: z.string(),
});
export const ShopperAgentReturnCreatedEventSchema: z.ZodType<ShopperAgentReturnCreatedEvent> =
  ShopperAgentReturnCreatedEventSchema_;
export interface ShopperAgentReturnCreatedEvent extends z.infer<
  typeof ShopperAgentReturnCreatedEventSchema_
> {}

const ShopperAgentOrderCancelledEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.SHOPPER_AGENT_ORDER_CANCELLED),
  ...shopperAgentAttributionFields,
  orderId: z.string(),
  cancellationReason: z.string(),
  refundAmount: redoStorefrontEventMoneySchema.optional(),
});
export const ShopperAgentOrderCancelledEventSchema: z.ZodType<ShopperAgentOrderCancelledEvent> =
  ShopperAgentOrderCancelledEventSchema_;
export interface ShopperAgentOrderCancelledEvent extends z.infer<
  typeof ShopperAgentOrderCancelledEventSchema_
> {}

const ShopperAgentOrderItemsUpdatedEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.SHOPPER_AGENT_ORDER_ITEMS_UPDATED),
    ...shopperAgentAttributionFields,
    orderId: z.string(),
    /** Signed: positive = customer owes more (upsell), negative = refund. */
    orderValueChange: redoStorefrontEventMoneySchema.optional(),
  });
export const ShopperAgentOrderItemsUpdatedEventSchema: z.ZodType<ShopperAgentOrderItemsUpdatedEvent> =
  ShopperAgentOrderItemsUpdatedEventSchema_;
export interface ShopperAgentOrderItemsUpdatedEvent extends z.infer<
  typeof ShopperAgentOrderItemsUpdatedEventSchema_
> {}

const ShopperAgentShippingAddressUpdatedEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(
      CustomerEventType.SHOPPER_AGENT_SHIPPING_ADDRESS_UPDATED,
    ),
    ...shopperAgentAttributionFields,
    orderId: z.string(),
  });
export const ShopperAgentShippingAddressUpdatedEventSchema: z.ZodType<ShopperAgentShippingAddressUpdatedEvent> =
  ShopperAgentShippingAddressUpdatedEventSchema_;
export interface ShopperAgentShippingAddressUpdatedEvent extends z.infer<
  typeof ShopperAgentShippingAddressUpdatedEventSchema_
> {}

const ShopperAgentReturnLabelResentEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.SHOPPER_AGENT_RETURN_LABEL_RESENT),
    ...shopperAgentAttributionFields,
    orderId: z.string(),
    returnIds: z.array(z.string()),
  });
export const ShopperAgentReturnLabelResentEventSchema: z.ZodType<ShopperAgentReturnLabelResentEvent> =
  ShopperAgentReturnLabelResentEventSchema_;
export interface ShopperAgentReturnLabelResentEvent extends z.infer<
  typeof ShopperAgentReturnLabelResentEventSchema_
> {}

const ShopperAgentGiftCardResentEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.SHOPPER_AGENT_GIFT_CARD_RESENT),
  ...shopperAgentAttributionFields,
  orderId: z.string(),
});
export const ShopperAgentGiftCardResentEventSchema: z.ZodType<ShopperAgentGiftCardResentEvent> =
  ShopperAgentGiftCardResentEventSchema_;
export interface ShopperAgentGiftCardResentEvent extends z.infer<
  typeof ShopperAgentGiftCardResentEventSchema_
> {}

const ShopperAgentDiscountCodeGeneratedEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(
      CustomerEventType.SHOPPER_AGENT_DISCOUNT_CODE_GENERATED,
    ),
    ...shopperAgentAttributionFields,
    discountId: z.string(),
    discountCode: z.string(),
  });
export const ShopperAgentDiscountCodeGeneratedEventSchema: z.ZodType<ShopperAgentDiscountCodeGeneratedEvent> =
  ShopperAgentDiscountCodeGeneratedEventSchema_;
export interface ShopperAgentDiscountCodeGeneratedEvent extends z.infer<
  typeof ShopperAgentDiscountCodeGeneratedEventSchema_
> {}

const ShopperAgentKnowledgeRetrievedEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.SHOPPER_AGENT_KNOWLEDGE_RETRIEVED),
    ...shopperAgentAttributionFields,
    query: z.string(),
    resultCount: z.number(),
  });
export const ShopperAgentKnowledgeRetrievedEventSchema: z.ZodType<ShopperAgentKnowledgeRetrievedEvent> =
  ShopperAgentKnowledgeRetrievedEventSchema_;
export interface ShopperAgentKnowledgeRetrievedEvent extends z.infer<
  typeof ShopperAgentKnowledgeRetrievedEventSchema_
> {}

const ShopperAgentOrderTrackingProvidedEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(
      CustomerEventType.SHOPPER_AGENT_ORDER_TRACKING_PROVIDED,
    ),
    ...shopperAgentAttributionFields,
    orderId: z.string(),
  });
export const ShopperAgentOrderTrackingProvidedEventSchema: z.ZodType<ShopperAgentOrderTrackingProvidedEvent> =
  ShopperAgentOrderTrackingProvidedEventSchema_;
export interface ShopperAgentOrderTrackingProvidedEvent extends z.infer<
  typeof ShopperAgentOrderTrackingProvidedEventSchema_
> {}

const ShopperAgentBackInStockSubscribedEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(
      CustomerEventType.SHOPPER_AGENT_BACK_IN_STOCK_SUBSCRIBED,
    ),
    ...shopperAgentAttributionFields,
    productId: z.string(),
    variantIds: z.array(z.string()),
  });
export const ShopperAgentBackInStockSubscribedEventSchema: z.ZodType<ShopperAgentBackInStockSubscribedEvent> =
  ShopperAgentBackInStockSubscribedEventSchema_;
export interface ShopperAgentBackInStockSubscribedEvent extends z.infer<
  typeof ShopperAgentBackInStockSubscribedEventSchema_
> {}

const ShopperAgentProductsSearchedEventSchema_ = baseCustomerEventSchema.extend(
  {
    eventType: z.literal(CustomerEventType.SHOPPER_AGENT_PRODUCTS_SEARCHED),
    ...shopperAgentAttributionFields,
    lookupType: z.string(),
    query: z.string().optional(),
    /** External (platform) product ids returned by the search. */
    productIds: z.array(z.string()),
    resultCount: z.number(),
  },
);
export const ShopperAgentProductsSearchedEventSchema: z.ZodType<ShopperAgentProductsSearchedEvent> =
  ShopperAgentProductsSearchedEventSchema_;
export interface ShopperAgentProductsSearchedEvent extends z.infer<
  typeof ShopperAgentProductsSearchedEventSchema_
> {}

const ShopperAgentLinkFollowedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.SHOPPER_AGENT_LINK_FOLLOWED),
  ...shopperAgentAttributionFields,
  /** Destination the shortened link redirected to. */
  url: z.string(),
});
export const ShopperAgentLinkFollowedEventSchema: z.ZodType<ShopperAgentLinkFollowedEvent> =
  ShopperAgentLinkFollowedEventSchema_;
export interface ShopperAgentLinkFollowedEvent extends z.infer<
  typeof ShopperAgentLinkFollowedEventSchema_
> {}

// ###
// ProductSubscriptionCreatedEvent
// ###
const ProductSubscriptionCreatedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.PRODUCT_SUBSCRIPTION_CREATED),
  /** Surface that triggered the subscription creation */
  source: z.enum(["email", "tracking_page"]),
  /** Full Shopify GID (e.g., "gid://shopify/Product/123") */
  productId: z.string(),
  /** Full Shopify GID (e.g., "gid://shopify/ProductVariant/456") */
  variantId: z.string(),
  /** Full Shopify selling plan GID selected by the customer. Null when created via tracking page (Recharge/Skio direct, no Shopify selling plan). */
  sellingPlanId: z.string().nullish(),
  /** Subscription ID from Recharge/Skio. "pending" when stubbed before integration is wired. */
  subscriptionId: z.string(),
  /** Which subscription platform created the subscription. */
  subscriptionPlatform: createEnum(SubscriptionIntegrationType),
  /** Present when triggered from an order context (e.g., email or tracking page) */
  orderId: z.string().nullish(),
  /** Present when triggered from an interactive email */
  emailId: z.string().nullish(),
});
export const ProductSubscriptionCreatedEventSchema: z.ZodType<ProductSubscriptionCreatedEvent> =
  ProductSubscriptionCreatedEventSchema_;
export interface ProductSubscriptionCreatedEvent extends z.infer<
  typeof ProductSubscriptionCreatedEventSchema_
> {}

// ###
// ProductSubscriptionCancelledCustomerEvent
// ###
/**
 * One line on a cancelled subscription. Platforms vary — Recharge/Skio always
 * carry one line per subscription, but Shopify SubscriptionContracts and
 * Ordergroove bundles can carry multiple.
 */
const subscriptionCancelledLineSchema = z.object({
  /** Product id as known by the event's `storefrontProvider`. */
  externalProductId: z.string().nullish(),
  /** Variant id as known by the event's `storefrontProvider`. */
  externalVariantId: z.string().nullish(),
  productTitle: z.string().nullish(),
  variantTitle: z.string().nullish(),
  /** Per-unit price for this line. */
  price: redoStorefrontEventMoneySchema,
  /** Quantity per recurring charge for this line. */
  quantity: z.number(),
});

const ProductSubscriptionCancelledCustomerEventSchema_ =
  baseCustomerEventSchema.extend({
    eventType: z.literal(CustomerEventType.PRODUCT_SUBSCRIPTION_CANCELLED),
    /** Which subscription platform owns the cancelled subscription. */
    subscriptionPlatform: createEnum(SubscriptionIntegrationType),
    /** The platform's subscription id, stringified. */
    subscriptionId: z.string(),
    /** The underlying storefront/commerce platform whose ids appear in `lines[].external*Id`. */
    storefrontProvider: createEnum(Provider, { enumName: "Provider" }),
    /** Line items on the cancelled subscription. Always at least one. */
    lines: z.array(subscriptionCancelledLineSchema).min(1),
    /** Vendor's verbatim cancellation reason string. Null if the vendor didn't supply one. */
    cancellationReason: z.string().nullish(),
    /** Free-form comments the vendor supplied alongside the reason. */
    cancellationReasonComments: z.string().nullish(),
    /** Verbatim vendor webhook payload, for downstream consumers that need fields we haven't normalized. */
    rawEvent: z.unknown(),
  });
export const ProductSubscriptionCancelledCustomerEventSchema: z.ZodType<ProductSubscriptionCancelledCustomerEvent> =
  ProductSubscriptionCancelledCustomerEventSchema_;
export interface ProductSubscriptionCancelledCustomerEvent extends z.infer<
  typeof ProductSubscriptionCancelledCustomerEventSchema_
> {}

// ###
// PriceDropInterestInferredEvent
// ###
const priceDropProductSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
  productTitle: z.string(),
  variantTitle: z.string().nullish(),
  imageUrl: z.string().nullish(),
});

const priceChangeSchema = z.object({
  previousPrice: z.number(),
  currentPrice: z.number(),
  priceChangeAmount: z.number(),
  priceChangePercentage: z.number(),
  currencyCode: z.string(),
});

const PriceDropInterestInferredEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.PRICE_DROP_INTEREST_INFERRED),
  provider: createEnum(Provider, { enumName: "Provider" }),
  product: priceDropProductSchema,
  priceChange: priceChangeSchema,
  engagementEventCount: z.number().int().nonnegative(),
});
export const PriceDropInterestInferredEventSchema: z.ZodType<PriceDropInterestInferredEvent> =
  PriceDropInterestInferredEventSchema_;
export interface PriceDropInterestInferredEvent extends z.infer<
  typeof PriceDropInterestInferredEventSchema_
> {}

// ###
// RefundReturnSubmittedEvent
// ###
const RefundReturnSubmittedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.REFUND_RETURN_SUBMITTED),
  returnId: zExt.objectId(),
});
export const RefundReturnSubmittedEventSchema: z.ZodType<RefundReturnSubmittedEvent> =
  RefundReturnSubmittedEventSchema_;
export interface RefundReturnSubmittedEvent extends z.infer<
  typeof RefundReturnSubmittedEventSchema_
> {}

// ###
// ExchangeProcessedWithCreditEvent
// ###
const ExchangeProcessedWithCreditEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.EXCHANGE_PROCESSED_WITH_CREDIT),
  returnId: zExt.objectId(),
});
export const ExchangeProcessedWithCreditEventSchema: z.ZodType<ExchangeProcessedWithCreditEvent> =
  ExchangeProcessedWithCreditEventSchema_;
export interface ExchangeProcessedWithCreditEvent extends z.infer<
  typeof ExchangeProcessedWithCreditEventSchema_
> {}

// ###
// WarrantyRegisteredEvent
// ###
const WarrantyRegisteredEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.WARRANTY_REGISTERED),
  warrantyRegistrationId: zExt.objectId(),
  discountCodes: attributableDiscountCodesSchema,
});
export const WarrantyRegisteredEventSchema: z.ZodType<WarrantyRegisteredEvent> =
  WarrantyRegisteredEventSchema_;
export interface WarrantyRegisteredEvent extends z.infer<
  typeof WarrantyRegisteredEventSchema_
> {}
// PostPurchasePageViewedEvent
// ###
const postPurchaseVariantOfferSchema = z.object({
  variantGid: z.string(),
  variantTitle: z.string(),
  discount: z
    .object({
      value: z.number(),
      valueType: createEnum(["percentage", "fixed_amount"]),
    })
    .optional(),
});

const postPurchaseOfferSchema = z.object({
  productGid: z.string(),
  productTitle: z.string(),
  variants: z.array(postPurchaseVariantOfferSchema),
});
const postPurchaseAcceptedOfferSchema = postPurchaseOfferSchema
  .omit({ variants: true })
  .extend({ selectedVariant: postPurchaseVariantOfferSchema });

const splitInfoSchema = z.object({
  splitId: z.string(),
  treatmentId: z.string(),
});

const PostPurchasePageViewedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.POST_PURCHASE_PAGE_VIEWED),
  referenceId: z.string(),
  upsellPageId: z.string(),
  nodeId: z.string(),
  splits: z.array(splitInfoSchema).optional(),
  offers: z.array(postPurchaseOfferSchema).optional(),
});
export const PostPurchasePageViewedEventSchema: z.ZodType<PostPurchasePageViewedEvent> =
  PostPurchasePageViewedEventSchema_;
export interface PostPurchasePageViewedEvent extends z.infer<
  typeof PostPurchasePageViewedEventSchema_
> {}

// ###
// TrackingPageViewedEvent
// ###
// Payload fields are the single source of truth in `widget-event-payloads`
// (browser-importable); the customer event adds the base envelope + eventType.
const TrackingPageViewedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.TRACKING_PAGE_VIEWED),
  ipAddress: z.string().optional(),
  ...trackingPageViewedPayloadSchema.shape,
});
export const TrackingPageViewedEventSchema: z.ZodType<TrackingPageViewedEvent> =
  TrackingPageViewedEventSchema_;
export interface TrackingPageViewedEvent extends z.infer<
  typeof TrackingPageViewedEventSchema_
> {}

// ###
// TrackingPageClickedEvent
// ###
const TrackingPageClickedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.TRACKING_PAGE_CLICKED),
  ipAddress: z.string().optional(),
  ...trackingPageClickedPayloadSchema.shape,
});
export const TrackingPageClickedEventSchema: z.ZodType<TrackingPageClickedEvent> =
  TrackingPageClickedEventSchema_;
export interface TrackingPageClickedEvent extends z.infer<
  typeof TrackingPageClickedEventSchema_
> {}

// ###
// PostPurchaseOfferAcceptedEvent
// ###
const PostPurchaseOfferAcceptedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.POST_PURCHASE_OFFER_ACCEPTED),
  referenceId: z.string(),
  nodeId: z.string(),
  upsellPageId: z.string().optional(),
  splits: z.array(splitInfoSchema).optional(),
  offer: postPurchaseAcceptedOfferSchema,
  revenueInShopCurrency: z.number(),
});
export const PostPurchaseOfferAcceptedEventSchema: z.ZodType<PostPurchaseOfferAcceptedEvent> =
  PostPurchaseOfferAcceptedEventSchema_;
export interface PostPurchaseOfferAcceptedEvent extends z.infer<
  typeof PostPurchaseOfferAcceptedEventSchema_
> {}

// ###
// ReviewCreatedEvent
// ###
const ReviewCreatedEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.REVIEW_CREATED),
  reviewId: z.string(),
  shopifyProductId: z.string().optional(),
  score: z.number().optional(),
  title: z.string().optional(),
  content: z.string().optional(),
  productTitle: z.string().optional(),
  verifiedBuyer: z.boolean().optional(),
});
export const ReviewCreatedEventSchema: z.ZodType<ReviewCreatedEvent> =
  ReviewCreatedEventSchema_;
export interface ReviewCreatedEvent extends z.infer<
  typeof ReviewCreatedEventSchema_
> {}

// ###
// CustomEventEvent
// ###
const CustomEventEventSchema_ = baseCustomerEventSchema.extend({
  eventType: z.literal(CustomerEventType.CUSTOM_EVENT),
  ipAddress: z.string().optional(),
  ...customEventPayloadSchema.shape,
  uniqueId: z.string().optional(), // TODO: why not using existing idempotencyKey field?
});
export const CustomEventEventSchema: z.ZodType<CustomEventEvent> =
  CustomEventEventSchema_;
export interface CustomEventEvent extends z.infer<
  typeof CustomEventEventSchema_
> {}

const customerEventSchemas = [
  OrderCreatedCustomerEventSchema_,
  CheckoutRatesShownCustomerEventSchema_,
  OrderUpdatedCustomerEventSchema_,
  OrderStatusUpdatedCustomerEventSchema_,
  ReturnCreatedCustomerEventSchema,
  ReturnStatusUpdatedCustomerEventSchema,
  ReturnCommentCreatedCustomerEventSchema,
  ClaimCreatedCustomerEventSchema,
  ClaimStatusUpdatedCustomerEventSchema,
  ClaimCommentCreatedCustomerEventSchema,
  ConversationCreatedCustomerEventSchema,
  ConversationStatusUpdatedCustomerEventSchema,
  ConversationMessageCreatedCustomerEventSchema,
  ExternalPlatformCustomerCreatedCustomerEventSchema,
  StorefrontLoginCustomerEventSchema,
  MarketingSubscribeEventSchema,
  MarketingUnsubscribeEventSchema,
  MarketingConfirmEventSchema,
  ProductViewedShoppingEventSchema_,
  ProductAddedToCartShoppingEventSchema_,
  ProductRemovedFromCartShoppingEventSchema_,
  CheckoutContactInfoSubmittedShoppingEventSchema_,
  CheckoutStartedShoppingEventSchema_,
  CheckoutCompletedShoppingEventSchema_,
  PaymentFailedShoppingEventSchema_,
  StorePageViewedShoppingEventSchema_,
  CollectionViewedShoppingEventSchema_,
  CheckoutInfoChangedShoppingEventSchema_,
  CheckoutProbabilityCalculatedShoppingEventSchema_,
  PackageProtectionPriceExposedShoppingEventSchema_,
  InboundReceivedEventSchema_,
  OutreachBounceEventSchema_,
  OutreachClickEventSchema_,
  OutreachComplaintEventSchema_,
  OutreachDeliveryEventSchema_,
  OutreachFailedEventSchema_,
  OutreachOpenEventSchema_,
  OutreachSendEventSchema_,
  CustomerGroupEnterEventSchema,
  CustomerGroupExitEventSchema,
  CustomerInformationUpdatedEventSchema,
  ReturnProcessedCustomerEventSchema,
  OmsOrderReadyForPickupEventSchema,
  LoyaltyPointsEarnEventSchema,
  LoyaltyPointsEarnReviewEventSchema,
  LoyaltyPointsEarnPurchaseEventSchema,
  LoyaltyPointsRedeemEventSchema,
  LoyaltyPointsReturnRefundEventSchema,
  LoyaltyPointsReturnClawbackEventSchema,
  LoyaltyPointsCancelPurchaseRefundEventSchema,
  LoyaltyPointsCancelPurchaseClawbackEventSchema,
  LoyaltyPointsManualAdjustmentEventSchema,
  LoyaltyPointsExpireEventSchema,
  AddressVerificationFailedEventSchema,
  ProductSubscriptionActionEventSchema,
  SubscribedToBackInStockEventSchema_,
  TriggeredBackInStockNotificationEventSchema_,
  PersonaAssignmentEventSchema_,
  SignupFormViewedEventSchema_,
  SignupFormSubmittedEventSchema_,
  RescuePanelViewedEventSchema_,
  RescuePanelCtaClickedEventSchema_,
  FailedDiscountDetectedEventSchema_,
  FailedDiscountRescueClaimedEventSchema_,
  ProductVisualizationGeneratedEventSchema_,
  MeaningfulRecoverConversationEventSchema_,
  ShopperAgentReturnCreatedEventSchema_,
  ShopperAgentOrderCancelledEventSchema_,
  ShopperAgentOrderItemsUpdatedEventSchema_,
  ShopperAgentShippingAddressUpdatedEventSchema_,
  ShopperAgentReturnLabelResentEventSchema_,
  ShopperAgentGiftCardResentEventSchema_,
  ShopperAgentDiscountCodeGeneratedEventSchema_,
  ShopperAgentKnowledgeRetrievedEventSchema_,
  ShopperAgentOrderTrackingProvidedEventSchema_,
  ShopperAgentBackInStockSubscribedEventSchema_,
  ShopperAgentProductsSearchedEventSchema_,
  ShopperAgentLinkFollowedEventSchema_,
  ProductSubscriptionCreatedEventSchema_,
  ProductSubscriptionCancelledCustomerEventSchema_,
  PriceDropInterestInferredEventSchema_,
  RefundReturnSubmittedEventSchema_,
  ExchangeProcessedWithCreditEventSchema_,
  WarrantyRegisteredEventSchema_,
  PostPurchasePageViewedEventSchema_,
  PostPurchaseOfferAcceptedEventSchema_,
  TrackingPageViewedEventSchema_,
  TrackingPageClickedEventSchema_,
  ReviewCreatedEventSchema_,
  CustomEventEventSchema_,
] as const;
export const CustomerEventSchema: z.ZodType<CustomerEvent> =
  z.discriminatedUnion("eventType", customerEventSchemas);
export const NonDiscriminatedCustomerEventSchema: z.ZodType<CustomerEvent> =
  z.union(customerEventSchemas);

type _SchemaEventType = z.infer<typeof CustomerEventSchema>["eventType"];
type _MissingEventTypes = Exclude<CustomerEventType, _SchemaEventType>;
export const _exhaustivenessCheck: _MissingEventTypes extends never
  ? true
  : _MissingEventTypes = true;
